import { type ID, selectNodeParent } from '@octopus-do/octopus-project-core'
import { migrationProjectSelector } from '@store/migrationSelectors/projectSelectors'
import { SeoActions } from '@store/reducers/seo/actions'
import type { SeoFormValues } from '@store/reducers/seo/types'
import { UploadResultActions } from '@store/uploadResultSaga'
import { ErrorNotification } from '@storeOld/reducers/notification'
import noop from 'lodash/noop'
import React, { type ChangeEvent, type DragEvent, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'

import { GtmTriggerName, sendGtmEvent } from '@/constants/gtm'
import { getFileUploader, isTooLargeToUpload } from '@/services/upload'
import {
  getTooLargeMessage,
  getUploadErrorMessage,
} from '@/services/uploadError'
import type { AppState, AttachmentItem } from '@/types'

const imageFormats = ['image/png', 'image/jpg', 'image/jpeg', 'image/webp']

export const useUploadOpenGraph = (nodeId: ID) => {
  const dispatch = useDispatch()

  const [dragActive, setDragActive] = React.useState(false)
  const [error, setError] = useState(false)
  const [loading, setLoading] = useState(false)

  const projectId = useSelector<AppState, ID | null>(
    (state) => state.manager.meta.id
  )
  const form = useSelector<AppState, SeoFormValues>(({ seoForm }) => seoForm)
  const parentIds = useSelector(selectNodeParent(nodeId))
  const parentId = parentIds[0] || null

  const node = useSelector(
    parentId
      ? migrationProjectSelector.getProjectNodeById(parentId)
      : () => null
  )

  const handleUploadFileResult = async (data: AttachmentItem[]) => {
    dispatch(UploadResultActions.success(data[0]))
  }

  const handleDrag = (e: DragEvent<HTMLDivElement | HTMLFormElement>) => {
    e.preventDefault()
    e.stopPropagation()

    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true)
      setError(false)
    } else if (e.type === 'dragleave') {
      setDragActive(false)
    }
  }

  // A failed upload used to leave the spinner on for good: the flag was lowered
  // only on the happy path, and nothing told the person what went wrong.
  const handleUploadFile = async (file: File) => {
    const uploader = getFileUploader(projectId, handleUploadFileResult)

    if (!uploader) {
      return
    }

    if (isTooLargeToUpload(file)) {
      dispatch(ErrorNotification(getTooLargeMessage()))
      return
    }

    setLoading(true)

    try {
      const data = await uploader(file, noop)

      dispatch(
        SeoActions.change({
          id: nodeId,
          form: { ...form, image: { id: data.id, value: data.url } },
        })
      )
    } catch (uploadError) {
      dispatch(ErrorNotification(getUploadErrorMessage(uploadError)))
    } finally {
      setLoading(false)
    }
  }

  const handleDrop = async (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()

    setDragActive(false)

    sendGtmEvent(
      GtmTriggerName.SEO_OG_IMAGE_UPLOAD,
      'Add/Changing image of og:image by drop'
    )

    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0]

      if (imageFormats.includes(file.type)) {
        await handleUploadFile(file)

        setError(false)
      } else {
        setError(true)
      }
    }
  }

  const handleChange = async (e: ChangeEvent<HTMLInputElement>) => {
    e.preventDefault()

    sendGtmEvent(
      GtmTriggerName.SEO_OG_IMAGE_UPLOAD,
      'Add/Changing image of og:image'
    )

    if (e.target.files && e.target.files[0]) {
      await handleUploadFile(e.target.files[0])
    }
  }

  const handleUseParent = () => {
    const seoImage = node?.notes?.seoImage

    if (!seoImage) {
      return
    }

    const { id, value } = seoImage

    dispatch(
      SeoActions.change({
        id: nodeId,
        form: { ...form, image: { id, value } },
      })
    )
  }

  return {
    dragActive,
    error,
    withParent: !!node?.notes?.seoImage,
    loading,
    handleDrag,
    handleDrop,
    handleChange,
    handleUseParent,
  }
}
